// @flow
import { always, equals, curry } from "ramda"
import { parse, differenceInMilliseconds, isAfter, differenceInHours } from "date-fns"
import type { CurriedFunction2 } from "ramda"

import { get } from "common/utils/get"
import { US_CUBA_REGULATION_COUNTRIES } from "booking/scenes/ReservationForm/consts/reservationConsts"
import defaultFlight from "./Flight"
import type { Flight } from "./Flight"

export const BOARDING_PASS_AVAILABILITY_NEVER = "never"

export const _getDuration = (aUtc: ?Date, bUtc: ?Date) => differenceInMilliseconds(aUtc, bUtc)

export function departureArrivalFromJS(da: any) {
  return {
    where: da.where,
    when: {
      local: parse(da.when.local),
      utc: parse(da.when.utc),
    },
  }
}

export const createFlight = (data: any) => {
  const naturalId = [
    data.number,
    data.airline && data.airline.code,
    data.departure && data.departure.where.code,
    data.departure && (get(["departure", "when", "utc"], data) || new Date()).valueOf(),
    data.arrival && data.arrival.where.code,
    data.arrival && (get(["arrival", "when", "utc"], data) || new Date()).valueOf(),
  ].join(":")

  return {
    ...defaultFlight,
    id: naturalId,
    naturalId,
    ...data,
  }
}

// ACHTUNG: returns duration in milliseconds
export const getDuration = (flight: Flight) =>
  _getDuration(get(["arrival", "when", "utc"], flight), get(["departure", "when", "utc"], flight))

export const hasDeparted = (flight: Flight): boolean => {
  const now = new Date()
  return isAfter(now, get(["departure", "when", "utc"], flight))
}

export const hasArrived = (flight: Flight): boolean => {
  const now = new Date()

  return isAfter(now, ["arrival", "when", "utc"], flight)
}

export const getFrom = (flight: Flight): ?string => get(["departure", "where", "name"], flight)

export const getTo = (flight: Flight): ?string => get(["arrival", "where", "name"], flight)

export const getStopsCount: Flight => 0 = always(0)

export const getFlightsCount: Flight => 1 = always(1)

// mmb api does not return flight_no - unable to generate flight number
export const getFlightNumber = (flight: Flight): ?string =>
  flight.number ? `${flight.airline.code} ${flight.number}` : null

export const getHoursToDeparture = (flight: Flight) => {
  const departure = get(["departure", "when", "utc"], flight)
  return departure && differenceInHours(departure, new Date())
}

export const getClosingHours = get(["airline, closeBookingHours"])

export const hasDifferentOperatingAirline = (flight: Flight) =>
  flight.operatingAirline && flight.operatingAirline.name !== flight.airline.name

export const hasDelay = (flight: Flight): boolean =>
  equals("delayed", get(["status", "type"], flight))

export const getDelayedTimes = (flight: Flight) =>
  hasDelay(flight) && get(["status", "changedTime"], flight)

export const isFlightWithinCountry: CurriedFunction2<*, *, *> = curry(
  (country: string, flight: Flight) =>
    [
      get(["departure", "where", "country"], flight),
      get(["arrival", "where", "country"], flight),
    ].every(x => x === country),
)

// Check if this flight is affected by `Cuba <=> US territories` regulations
export const isCubaRegulationAffected = (flight: Flight) => {
  const departureCountry = get(["departure", "where", "country"], flight)
  const arrivalCountry = get(["arrival", "where", "country"], flight)

  return (
    (arrivalCountry === "CU" && US_CUBA_REGULATION_COUNTRIES.includes(departureCountry)) ||
    (departureCountry === "CU" && US_CUBA_REGULATION_COUNTRIES.includes(arrivalCountry))
  )
}

// eslint-disable-next-line flowtype/no-weak-types
export const convertFromApi = (input: Object) =>
  createFlight({
    ...input,
    departure: departureArrivalFromJS(input.departure),
    arrival: departureArrivalFromJS(input.arrival),
  })
